import React, { Fragment } from "react"
import { useTranslation } from "react-i18next"
import { format, parseISO, formatDistanceToNow, addDays } from "date-fns"
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useToast } from "@chakra-ui/react"

import { trackPage } from "../../../services/analytics"
import { addFlightToCart } from "../../../services/api"

import bellImage from "../../../img/bell.svg"
import QFLogo from "../../../img/QF.svg"
import VALogo from "../../../img/VA.svg"
import AVLogo from "../../../img/AV.svg"
import AALogo from "../../../img/AA.svg"
import UALogo from "../../../img/UA.svg"
import APLogo from "../../../img/AP.svg"

import {
  Text,
  Tr,
  Td,
  Image,
  Show,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverCloseButton,
  Box,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react"
import {
  AIRPLANE_COLORS,
  COLORS,
  DATE_FORMAT_AUSTRALIA_DEPART,
  flightClassesMapping,
} from "../../../constants/constants"

import ExpandableRow from "./ExpandableRow"
import AirplaneIcon from "../../../img/DynamicSVGs"
import { formatTime, formatUTCDate } from "../../../helpers/functions"
import AlertRouteContent from "../../AlertRouteContent"
import BookingButton from "../../BookingButton"

const numberFormat = new Intl.NumberFormat()

const parseDate = (dateStr) => {
  return parseISO(dateStr)
}

const airlineLogos = {
  QF: QFLogo,
  VA: VALogo,
  AV: AVLogo,
  AA: AALogo,
  UA: UALogo,
  AP: APLogo,
}

const FlightRow = ({
  flight,
  user,
  expandedFlight,
  setExpandedFlight,
  isMonthlyOrAnnual,
  originRegionName,
  destinationRegionName,
  userCarts,
}) => {
  const { t } = useTranslation()
  const placement = useBreakpointValue({ base: "bottom", sm: "left" })
  const airplaneIconSize = useBreakpointValue({ base: 14, sm: 16 })
  const queryClient = useQueryClient()
  const toast = useToast()

  const canCreateAlerts = user?.remaining_alerts || 0

  const { details } = flight

  const summaryPoints = flight.class_details.reduce(
    (acc, item) => ({
      ...acc,
      [item.designated_class]: {
        points: item.points_per_adult,
        name: t(`table.${flightClassesMapping[item.designated_class]}`),
        tax_currency: item.tax_currency,
        tax_per_adult: item.tax_per_adult,
        remaining_seats: item.remaining_seats,
        mixed_cabin: item.mixed_cabin,
      },
    }),
    {}
  )

  function getDayDifference(departureDate, arrivalDate) {
    const departureDay = parseInt(departureDate.split("T")[0].split("-")[2], 10)
    const arrivalDay = parseInt(arrivalDate.split("T")[0].split("-")[2], 10)

    return Math.abs(arrivalDay - departureDay)
  }

  const diffInDays = getDayDifference(
    details[0].departure_date,
    details[details.length - 1].arrival_date
  )

  const route = {
    origin: flight.origin,
    destination: flight.destination,
    startDate: format(parseISO(flight.flight_start_date), "yyyy-MM-dd"),
    endDate: format(
      addDays(parseISO(flight.flight_start_date), 1),
      "yyyy-MM-dd"
    ),
    flightClasses: Object.keys(summaryPoints),
    preferredPrograms: [flight.source.toLowerCase() || "qf"],
    source: [flight.source],
    originRegion: { name: originRegionName, enabled: false },
    destinationRegion: { name: destinationRegionName, enabled: false },
    isNew: true,
    showRegionCheckboxes: isMonthlyOrAnnual,
    sms_alert: false,
    email_alert: false,
  }

  const isFlightExpanded = expandedFlight === flight

  const lastSeenText = formatDistanceToNow(parseDate(flight.timestamp), {
    addSuffix: true,
  })
  const shouldIncludeAbout =
    !lastSeenText.includes("about") && lastSeenText.includes("ago")

  const { mutateAsync: addToCartMutation, isLoading: isAddingToCart } =
    useMutation({
      mutationFn: addFlightToCart,
      onSuccess: () => {
        toast({
          title: t("cart.addSuccess") || "Flight added to cart successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        })
        queryClient.invalidateQueries(["userCarts"])
      },
      onError: (error) => {
        toast({
          title: t("cart.addError") || "Failed to add flight to cart",
          description: error.response?.data?.detail || error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        })
      },
    })

  const handleAddToTrip = async (e, designatedClass) => {
    e.stopPropagation()
    const classDetail = flight.class_details.find(
      (detail) => detail.designated_class === designatedClass
    )

    try {
      await addToCartMutation({ class_detail_id: classDetail.id })
    } catch (error) {
      console.error("Failed to add flight to cart:", error)
    }
  }

  return (
    <Fragment key={flight.id}>
      <Tr
        fontSize={[12, 14]}
        position={isFlightExpanded ? "relative" : "initial"}
        zIndex={12}
        backgroundColor={isFlightExpanded ? COLORS.grey : COLORS.white}
        boxShadow={
          isFlightExpanded
            ? "0px -10px 18px 0px rgba(20, 23, 37, 0.13)"
            : "none"
        }
        fontWeight="semibold"
        onClick={() => {
          setExpandedFlight(expandedFlight === flight ? null : flight)
        }}
        cursor="pointer"
      >
        <Td p={{ base: 3, lg: 4 }} fontWeight={600} fontSize={12}>
          {formatUTCDate(
            flight.details[0].departure_date,
            DATE_FORMAT_AUSTRALIA_DEPART
          )}
        </Td>
        <Td
          p={{ base: 3, lg: 4 }}
          width="50px"
          border={isFlightExpanded ? "none" : ""}
        >
          <Text fontSize={12}>{flight.origin.city}</Text>
          {window.innerWidth < 992 && (
            <Text fontSize={11} color={COLORS.secondary}>
              {formatTime(flight.flight_start_date)}
            </Text>
          )}
        </Td>
        <Td
          border={isFlightExpanded ? "none" : ""}
          p={{ base: 3, lg: 4 }}
          width="50px"
        >
          <Text fontSize={12}>{flight.destination.city}</Text>
          {window.innerWidth < 992 && (
            <Text fontSize={11} color={COLORS.secondary}>
              {formatTime(flight.flight_end_date)}
              {diffInDays > 0 && <Text as="sup"> +{diffInDays}</Text>}
            </Text>
          )}
        </Td>
        <Show above="lg">
          <Td p={{ base: 3, lg: 4 }} border={isFlightExpanded ? "none" : ""}>
            <Text>
              {formatTime(flight.flight_start_date)} -{" "}
              {formatTime(flight.flight_end_date)}
              {diffInDays > 0 && <Text as="sup"> +{diffInDays}</Text>}
            </Text>
            <Text fontSize={12}>
              {details
                .map((conn) => conn.aircraft_details.split("(")[0])
                .join(", ")}
            </Text>
          </Td>
        </Show>

        <Show above="lg">
          <Td
            p={{ base: 3, lg: 4 }}
            border={isFlightExpanded ? "none" : ""}
            fontSize={"12px"}
          >
            <Text>{flight.stopovers === 0 ? "Direct" : flight.stopovers}</Text>
            <Text fontSize={12} color={COLORS.secondary}>
              {details
                .slice(0, -1)
                .map((conn) => conn.to_airport.match(/\(([^)]+)\)$/)[1])
                .join(", ")}
            </Text>
          </Td>
          <Td p={{ base: 3, lg: 4 }} border={isFlightExpanded ? "none" : ""}>
            {summaryPoints["Business"] ? (
              <Box position={"relative"}>
                {summaryPoints["Business"].mixed_cabin && (
                  <Text
                    position={"absolute"}
                    left={"-12px"}
                    top={"-4px"}
                    fontSize={"14px"}
                  >
                    *
                  </Text>
                )}
                <Text color={COLORS.red}>
                  {numberFormat.format(summaryPoints["Business"].points)}
                  <Text as="span" fontSize={10}>
                    {" "}
                    +{summaryPoints["Business"].tax_currency}
                    {Math.round(summaryPoints["Business"].tax_per_adult)}
                  </Text>
                </Text>
                <Text color={COLORS.black300} fontSize="xs">
                  {summaryPoints["Business"].remaining_seats}
                </Text>
              </Box>
            ) : (
              "-"
            )}
          </Td>
          <Td p={{ base: 3, lg: 4 }} border={isFlightExpanded ? "none" : ""}>
            {summaryPoints["First"] ? (
              <Box position={"relative"}>
                {summaryPoints["First"].mixed_cabin && (
                  <Text
                    position={"absolute"}
                    left={"-12px"}
                    top={"-4px"}
                    fontSize={"14px"}
                  >
                    *
                  </Text>
                )}
                <Text color={COLORS.red}>
                  {numberFormat.format(summaryPoints["First"].points)}
                  <Text as="span" fontSize={10}>
                    {" "}
                    +{summaryPoints["First"].tax_currency}
                    {Math.round(summaryPoints["First"].tax_per_adult)}
                  </Text>
                </Text>
                <Text color={COLORS.black300} fontSize="xs">
                  {summaryPoints["First"].remaining_seats}
                </Text>
              </Box>
            ) : (
              "-"
            )}
          </Td>
        </Show>

        <Show above={"lg"}>
          <Td
            p={{ base: 3, lg: 4 }}
            border={isFlightExpanded ? "none" : ""}
            fontSize={"12px"}
          >
            <Text color={COLORS.secondary}>
              {shouldIncludeAbout ? "about " : ""}
              {lastSeenText}
            </Text>
          </Td>
        </Show>
        <Show above="lg">
          <Td
            p={{ base: 3, lg: 4 }}
            border={isFlightExpanded ? "none" : ""}
            textAlign={"center"}
          >
            <BookingButton
              source={flight.source}
              url={flight.deep_url}
              to={flight.destination_city}
            />
          </Td>
        </Show>
        <Show below="lg">
          <Td
            textTransform="none"
            textAlign="center"
            p={{ base: 3, lg: 4 }}
            w={{ base: "10%" }}
          >
            <Flex
              flexDirection={"column"}
              gap={"2px"}
              alignItems={"flex-start"}
              height={"100%"}
            >
              {flight.class_details
                .filter((flightClass, index, self) => {
                  return (
                    index ===
                    self.findIndex(
                      (t) => t.designated_class === flightClass.designated_class
                    )
                  )
                })
                .map((flightClass) => {
                  const show =
                    flightClass.designated_class === "Business" ||
                    flightClass.designated_class === "First"

                  if (!show) return null

                  return (
                    <BookingButton
                      key={flightClass.id}
                      source={flight.source}
                      url={flight.deep_url}
                      to={flight.destination_city}
                      height="22px"
                      border={"none"}
                      backgroundColor={"transparent"}
                      boxShadow={"none"}
                    >
                      <AirplaneIcon
                        color={AIRPLANE_COLORS[flightClass.designated_class]}
                        width={airplaneIconSize}
                        height={airplaneIconSize}
                      />
                      <Text fontSize={{ base: 10, sm: 12 }} color={COLORS.red}>
                        <Text as={"span"} marginLeft={"2px"}>
                          {numberFormat.format(flightClass.points_per_adult)}
                        </Text>
                        <Text as={"span"} marginLeft={"2px"}>
                          +{flightClass.tax_currency}
                          {parseInt(flightClass.tax_per_adult)}
                        </Text>
                      </Text>
                    </BookingButton>
                  )
                })}
            </Flex>
          </Td>
        </Show>

        <Td
          p={{ base: 3, lg: 4 }}
          border={isFlightExpanded ? "none" : ""}
          sx={{
            "& > div": {
              minWidth: "unset!important",
            },
          }}
        >
          <Popover
            placement={placement}
            closeOnBlur={true}
            onOpen={() => {
              trackPage({
                title: "Alert Route",
              })
            }}
          >
            {({ onClose }) => (
              <>
                <PopoverTrigger>
                  <Image
                    src={bellImage}
                    alt={`create_reward_flight_alerts_from_${flight.origin.name}_to_${flight.destination.name}`}
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    margin="0 auto"
                  />
                </PopoverTrigger>
                <PopoverContent
                  _focus={{ boxShadow: "none" }}
                  boxShadow="0px 10px 22px rgba(0, 0, 0, 0.14);"
                  borderRadius={8}
                  w={{ base: "100%", sm: 360 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {!!canCreateAlerts && <PopoverCloseButton />}
                  <PopoverBody p={0}>
                    {!!canCreateAlerts ? (
                      <AlertRouteContent route={route} onClose={onClose} />
                    ) : (
                      <Box>
                        <Text>{t("table.maxAlertReached")}</Text>
                      </Box>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </>
            )}
          </Popover>
        </Td>
        <Show above={"lg"}>
          <Td>
            {isFlightExpanded ? (
              <ChevronUpIcon boxSize={6} />
            ) : (
              <ChevronDownIcon boxSize={6} />
            )}
          </Td>
        </Show>
        <Show below={"lg"}>
          <Td textTransform={"none"} textAlign={"center"} w={{ base: "10%" }}>
            <Image
              src={airlineLogos[flight.source] || QFLogo}
              alt={`${flight.source} logo`}
              width={6}
              height={6}
              mx={"auto"}
            />
          </Td>
        </Show>
      </Tr>

      {isFlightExpanded && (
        <ExpandableRow
          flight={flight}
          handleAddToTrip={handleAddToTrip}
          userCarts={userCarts}
          isAddingToCart={isAddingToCart}
          canCreateFlightCart={!!user}
        />
      )}
    </Fragment>
  )
}

export default FlightRow
